import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { EVENT_LABEL_CATALOGUE, SET_EVENT_LABELS } from "../gql/queries/eventMatching";

interface Label {
  key: string;
  name: string;
  family: string;
}

const FAMILIES: [string, string][] = [
  ["topic", "Topic"],
  ["skill", "English skill"],
];

/**
 * Tick the labels an event should carry. Saving pins them for every event in
 * the same series (same title, ignoring numbers like "Part 3"), so one fix
 * covers every recurrence.
 */
const EventLabelEditor: React.FC<{
  title: string;
  current: string[];
  edited: boolean;
  onClose: () => void;
  onSaved: () => void;
}> = ({ title, current, edited, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [picked, setPicked] = useState<Set<string>>(new Set(current));
  const { data, loading } = useQuery(EVENT_LABEL_CATALOGUE);
  const [save, { loading: saving }] = useMutation(SET_EVENT_LABELS);
  const catalogue: Label[] = data?.adminEventLabelCatalogue ?? [];

  const submit = async (labels: string[] | null) => {
    try {
      await save({ variables: { title, labels } });
      enqueueSnackbar(labels === null ? "Back to automatic labels." : "Labels saved.", {
        variant: "success",
      });
      onSaved();
    } catch (error) {
      enqueueSnackbar((error as Error).message, { variant: "error" });
    }
  };

  const toggle = (key: string) => {
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPicked(next);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit labels</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {title}
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
          Applies to every event in this series.
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          FAMILIES.map(([family, heading]) => (
            <Box key={family} mb={1}>
              <Typography variant="overline">{heading}</Typography>
              <Box display="flex" flexWrap="wrap">
                {catalogue
                  .filter((label) => label.family === family)
                  .map((label) => (
                    <FormControlLabel
                      key={label.key}
                      sx={{ width: { xs: "100%", sm: "50%" }, mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={picked.has(label.key)}
                          onChange={() => toggle(label.key)}
                        />
                      }
                      label={<Typography variant="body2">{label.name}</Typography>}
                    />
                  ))}
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        {edited && (
          <Button disabled={saving} onClick={() => submit(null)} sx={{ mr: "auto" }}>
            Back to automatic
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || loading}
          onClick={() => submit([...picked])}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventLabelEditor;
